import { AuthContext } from '@repo-level/core-front';
import { ImageComponent } from 'components/Image';
import { ProgressBarPalette } from 'components/progressBars/helpers';
import { SmallProgressBar } from 'components/progressBars/SmallProgressBar';
import { BonanzaContext } from 'contexts/bonanza';
import { BonanzaStatus } from 'helpers/bonanza';
import { useTheme } from 'hooks/theme';
import { PERCENT_BREACKPOINTS_BONANZA } from 'modules/bonanza/components/BoxSection/helpers';
import type { FC } from 'react';
import { useContext, useMemo } from 'react';
import { useNavigation } from 'react-navi';
import { ScBonanzaPromoSection } from './styled';

export interface BonanzaPromoSectionProps {
  isMobile?: boolean;
  onLinkClick?: () => void;
}

export const BonanzaPromoSection: FC<BonanzaPromoSectionProps> = ({
  isMobile = false,
  onLinkClick,
}) => {
  const { bonanzaReward, bonanzaData, bonanzaLevelItem, userBonanzaGroup } =
    useContext(BonanzaContext);
  const { isAuthenticated } = useContext(AuthContext);

  const { navigate } = useNavigation();
  const { images } = useTheme();

  const { isLockedPromo, isBonusIssued, isHighlighted } = useMemo(
    () => ({
      isLockedPromo: userBonanzaGroup === BonanzaStatus.PROMO_LOCKED,
      isBonusIssued: userBonanzaGroup === BonanzaStatus.BONUS_ISSUED,
      isHighlighted: [
        BonanzaStatus.LEVEL3,
        BonanzaStatus.LEVEL4,
        BonanzaStatus.BONUS_ISSUED,
      ].includes(userBonanzaGroup),
    }),
    [userBonanzaGroup]
  );

  const imgSrc = useMemo(() => {
    if (!bonanzaLevelItem) return '';
    const { labelMob, label } = bonanzaLevelItem;
    if (isAuthenticated) {
      return isMobile ? labelMob?.url : label?.url;
    }
    return isMobile ? bonanzaData?.menu?.unloggedMob.url : label?.url;
  }, [isAuthenticated, isMobile, bonanzaLevelItem, bonanzaData]);

  const imgConfig = useMemo(
    () => ({
      src: imgSrc || '',
      alt: 'logo',
      width: isMobile ? 56 : 128,
      height: isMobile ? 56 : 32,
    }),
    [isMobile, imgSrc]
  );
  const progressbar = (
    <SmallProgressBar
      config={{
        dotsCount: PERCENT_BREACKPOINTS_BONANZA.length,
        color: ProgressBarPalette.PURPLE,
      }}
      userProgress={bonanzaLevelItem?.progressPercent || 0}
    />
  );

  const isPromoAvailable = isAuthenticated && !isLockedPromo && !isBonusIssued;
  if (!(bonanzaData && bonanzaLevelItem)) return null;

  const {
    bg,
    yourProgress,
    yourReward,
    unLoggedLabelText,
    returnToStartFresh,
    openBonus,
  } = bonanzaData.menu;

  return (
    <ScBonanzaPromoSection
      bgMenu={bg.url}
      isHighlighted={isHighlighted}
      isMobile={isMobile}
      onClick={() => {
        onLinkClick && onLinkClick();
        navigate('/bonanza');
      }}
    >
      <div className="info">
        <div className="addons">
          {imgSrc && <ImageComponent {...imgConfig} />}
          {isMobile && isPromoAvailable && (
            <div className="text">
              <span>
                <b>{yourProgress}:</b>
                {bonanzaLevelItem.progressPercent}%
              </span>
              <span>
                <b>{yourReward}:</b>
                {bonanzaReward}
              </span>
              {isPromoAvailable && isMobile && progressbar}
            </div>
          )}
          {isAuthenticated ? (
            isLockedPromo ? (
              <p className="title">{returnToStartFresh}</p>
            ) : isBonusIssued ? (
              <p className="title">{openBonus}</p>
            ) : null
          ) : (
            <p className="title">{unLoggedLabelText}</p>
          )}
          {isMobile && <images.arrowRightSmallIcon.component />}
        </div>
      </div>

      {isPromoAvailable && !isMobile && (
        <>
          {progressbar}
          <p className="under-progress-text">
            {bonanzaLevelItem.progressPercent}%
          </p>
        </>
      )}
    </ScBonanzaPromoSection>
  );
};
